/**
 * The privacy panel (TMMAPP-0142 item 6; `GET …/{id}/privacy`), refreshed after each turn:
 * - **In scope:** the entities, and the data categories on and off;
 * - **Never sent:** the fixed statements, plus this session's counts, worded without naming anyone;
 * - **Sent:** per answer, a collapsible list of each call: the verbatim payload in a mono block and
 *   the data the assistant looked up (each tool call's input and result).
 * No money shows here (TMMAPP-0178 item 7): charges live only in the chat's Session details (the
 * usage, and the note that pictures and PDFs cost more to send than text), never as tokens (MM-D-140).
 *
 * In a shared session (TMMAPP-0142 U3; TMMAPI-0181 U6) the panel is everyone's: In scope adds each
 * member's own data as its banner names it, and each call says who asked. A
 * call purged because a contributor's data was erased reads "Removed: a contributor's data was
 * erased" in place of its payload, never "not sent".
 *
 * Attachments (TMMAPP-0142 U5 item 25c; TMMAPI-0181 U7): each call lists the files it sent (name,
 * size, pages or rows, cut to fit), and the members' files it left out with the reason.
 *
 * **AI tools** (TMMAPP-0142 L4; TMMAPI-0183 U1): every call a connected AI tool made in the
 * session, in its own section beside the assistant's calls. Each is collapsible like an answer's
 * calls: the connection's label and when, then the tool, what it asked and what it was given, as
 * JSON. A write reads "Changed something"; in a shared session each says whose AI tool it was. No
 * charge is shown (the tool's model isn't ours). A purged call reads "Removed: a contributor's data
 * was erased".
 */
import { Fragment, type ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type {
  AssistantAgentCall,
  AssistantCategoryOption,
  AssistantPrivacy,
  AssistantSentCall,
} from '@/api/assistant';
import { ErrorState } from '@/components/ErrorState';
import { ExpandIcon } from '@/components/icons';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { personName } from '@/i18n/personName';
import { resolveTokens } from '@/theme/theme';
import { alsoSentLabel, categoryList, privateDataBanner, statementLabel } from './assistantCopy';
import { AttachmentFacts } from './composer/AttachmentFacts';

/** Why a call left a member's file out: the served reason's sentence, else a generic one. */
const OMITTED_REASONS: ReadonlySet<string> = new Set(['REQUEST_BUDGET']);

function omittedReason(t: TFunction, reason: string): string {
  return t(`assistant.privacy.omittedReason.${OMITTED_REASONS.has(reason) ? reason : 'unknown'}`);
}

const COUNT_KEYS = [
  'peopleMasked',
  'namesMasked',
  'excludedLinesOmitted',
  'contributionsWithheld',
  'redactorMasks',
] as const;

function MonoBlock({ children }: { children: string }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.25,
        maxHeight: 280,
        overflow: 'auto',
        bgcolor: tokens.surface2,
        border: `1px solid ${tokens.line}`,
        borderRadius: 1,
        color: tokens.text,
      }}
    >
      <Typography
        variant="mono"
        component="code"
        sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
      >
        {children}
      </Typography>
    </Box>
  );
}

/** The AI tools section (L4): each connected AI tool's call, collapsible, with no charge. */
function AgentCalls({ calls, shared }: { calls: readonly AssistantAgentCall[]; shared: boolean }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const zone = useMe().user.timezone ?? 'UTC';
  const tokens = resolveTokens(useTheme());
  const sub = { fontSize: 12, color: tokens.text2 } as const;
  if (calls.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: tokens.text2 }} data-privacy-agent-empty="">
        {t('agentTools.privacy.empty')}
      </Typography>
    );
  }
  return (
    <Box data-privacy-agent-calls="">
      {calls.map((call, index) => (
        <Accordion key={`${call.at}-${index}`} disableGutters data-privacy-agent-call="">
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Stack sx={{ minWidth: 0, gap: 0.25 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, overflowWrap: 'anywhere' }}>
                {call.connectionLabel}
              </Typography>
              <Typography component="span" sx={sub}>
                {formatInstant(call.at, locale, zone)}
              </Typography>
              {shared && (
                <Typography component="span" sx={sub} data-privacy-agent-whose="">
                  {call.askedBy.mine
                    ? t('agentTools.privacy.yours')
                    : t('agentTools.privacy.theirs', { name: personName(call.askedBy.name, t) })}
                </Typography>
              )}
              {call.write && (
                <Box>
                  <Chip
                    size="small"
                    label={t('agentTools.privacy.wrote')}
                    data-privacy-agent-write=""
                  />
                </Box>
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="mono" component="p" sx={{ fontSize: 12, mb: 0.75 }}>
              {call.tool}
            </Typography>
            {call.contentRemoved ? (
              <Typography sx={{ fontSize: 13 }} data-privacy-removed="">
                {t('assistant.privacy.removed')}
              </Typography>
            ) : (
              <>
                <Typography sx={sub}>{t('agentTools.privacy.asked')}</Typography>
                <MonoBlock>{call.input}</MonoBlock>
                <Typography sx={{ ...sub, mt: 1 }}>{t('agentTools.privacy.given')}</Typography>
                <MonoBlock>{call.answer}</MonoBlock>
              </>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

interface PrivacyPanelProps {
  headingId: string;
  privacy: AssistantPrivacy | null;
  loadError: unknown;
  onRetry: () => void;
  /** A close control for the drawer or the one-pane layout. */
  close?: ReactNode;
  /** The category registry, for the served labels. */
  registry?: readonly AssistantCategoryOption[] | null | undefined;
  /** A shared session: each call says who asked. */
  shared?: boolean | undefined;
  /** The AI tools section (L4); Help & feedback can't be given to an AI tool, so it has none. */
  agentTools?: boolean | undefined;
}

export function PrivacyPanel({
  headingId,
  privacy,
  loadError,
  onRetry,
  close,
  registry,
  shared = false,
  agentTools = true,
}: PrivacyPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const section = { fontSize: 13, fontWeight: 600, color: tokens.text, mt: 2, mb: 1 } as const;
  const sub = { fontSize: 12, color: tokens.text2 } as const;

  // Calls grouped by answer, in the order they were sent.
  const turns: { turnId: number; calls: AssistantSentCall[] }[] = [];
  for (const call of privacy?.sent ?? []) {
    const group = turns.find((turn) => turn.turnId === call.turnId);
    if (group !== undefined) group.calls.push(call);
    else turns.push({ turnId: call.turnId, calls: [call] });
  }

  return (
    <Box sx={{ p: 2, minWidth: 0 }} data-privacy-panel="">
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          id={headingId}
          tabIndex={-1}
          variant="h2"
          sx={{ fontSize: 16, fontWeight: 600 }}
        >
          {t('assistant.region.privacy')}
        </Typography>
        {close}
      </Stack>
      {loadError !== null && privacy === null && (
        <Box sx={{ mt: 2 }}>
          <ErrorState error={loadError} what={t('assistant.privacy.loadError')} onRetry={onRetry} />
        </Box>
      )}
      {privacy === null && loadError === null && (
        <Typography sx={{ mt: 2, fontSize: 14, color: tokens.text2 }}>
          {t('common.loading')}
        </Typography>
      )}
      {privacy !== null && (
        <>
          <Typography component="h3" sx={section}>
            {t('assistant.privacy.inScope')}
          </Typography>
          <Typography sx={sub}>{t('assistant.privacy.entities')}</Typography>
          <Stack
            direction="row"
            sx={{ flexWrap: 'wrap', gap: 0.75, mt: 0.5, mb: 1 }}
            data-privacy-entities=""
          >
            {privacy.inScope.entities.length === 0 ? (
              <Typography sx={{ fontSize: 13 }}>{t('assistant.privacy.none')}</Typography>
            ) : (
              privacy.inScope.entities.map((entity) => (
                <Chip
                  key={`${entity.kind}:${entity.id}`}
                  size="small"
                  label={
                    entity.available && entity.name !== null
                      ? entity.name
                      : entity.name !== null
                        ? t('assistant.unavailableEntity', { name: entity.name })
                        : t('assistant.unavailableUnnamed')
                  }
                />
              ))
            )}
          </Stack>
          <Typography sx={sub}>{t('assistant.privacy.categoriesOn')}</Typography>
          <Typography sx={{ fontSize: 13, mb: 1 }} data-privacy-on="">
            {privacy.inScope.categoriesOn.length === 0
              ? t('assistant.privacy.none')
              : categoryList(t, privacy.inScope.categoriesOn, locale, registry)}
          </Typography>
          {/* What else the calls send beside the data on, as tmm-api lists it (TMMAPI-0206: the
              members' first names, on a shared session and a breakout only). */}
          {privacy.inScope.alsoSent.some((code) => alsoSentLabel(t, code) !== null) && (
            <Box
              component="ul"
              sx={{ m: 0, mb: 1, paddingInlineStart: 2.5, fontSize: 13 }}
              data-privacy-also-sent=""
            >
              {privacy.inScope.alsoSent.map((code) => {
                const label = alsoSentLabel(t, code);
                return label === null ? null : <li key={code}>{label}</li>;
              })}
            </Box>
          )}
          <Typography sx={sub}>{t('assistant.privacy.categoriesOff')}</Typography>
          <Typography sx={{ fontSize: 13 }} data-privacy-off="">
            {privacy.inScope.categoriesOff.length === 0
              ? t('assistant.privacy.none')
              : categoryList(t, privacy.inScope.categoriesOff, locale, registry)}
          </Typography>
          {privacy.inScope.privateData.length > 0 && (
            <>
              <Typography sx={{ ...sub, mt: 1 }}>{t('assistant.privacy.membersData')}</Typography>
              <Box
                component="ul"
                sx={{ m: 0, paddingInlineStart: 2.5, fontSize: 13 }}
                data-privacy-private-data=""
              >
                {privacy.inScope.privateData.map((item, index) => (
                  <li key={`${item.kind}:${item.accountId ?? item.category ?? ''}:${index}`}>
                    {privateDataBanner(t, item, registry)}
                  </li>
                ))}
              </Box>
            </>
          )}

          <Typography component="h3" sx={section}>
            {t('assistant.privacy.neverSent')}
          </Typography>
          <Box
            component="ul"
            sx={{ m: 0, paddingInlineStart: 2.5, fontSize: 13 }}
            data-privacy-never=""
          >
            {privacy.neverSent.statements.map((code) => {
              const label = statementLabel(t, code);
              return label === null ? null : <li key={code}>{label}</li>;
            })}
          </Box>
          {COUNT_KEYS.some((key) => privacy.neverSent.counts[key] > 0) && (
            <>
              <Typography sx={{ ...sub, mt: 1 }}>{t('assistant.privacy.countsHeading')}</Typography>
              <Box
                component="ul"
                sx={{ m: 0, paddingInlineStart: 2.5, fontSize: 13 }}
                data-privacy-counts=""
              >
                {COUNT_KEYS.filter((key) => privacy.neverSent.counts[key] > 0).map((key) => (
                  <li key={key}>
                    {t(`assistant.privacy.count.${key}`, { count: privacy.neverSent.counts[key] })}
                  </li>
                ))}
              </Box>
            </>
          )}

          <Typography component="h3" sx={section}>
            {t('assistant.privacy.sent')}
          </Typography>
          {turns.length === 0 && (
            <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
              {t('assistant.privacy.sentEmpty')}
            </Typography>
          )}
          <Box data-privacy-sent="">
            {turns.map((turn, index) => (
              <Accordion key={turn.turnId} disableGutters data-privacy-turn={turn.turnId}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                    {t('assistant.privacy.turnHeading', { n: index + 1 })}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {privacy.historyFromTurnId === turn.turnId && (
                    <Typography sx={{ fontSize: 12, color: tokens.text2, mb: 1 }}>
                      {t('assistant.privacy.historyBoundary')}
                    </Typography>
                  )}
                  <Stack spacing={2}>
                    {turn.calls.map((call, callIndex) => (
                      <Box key={call.aiRequestId} data-privacy-call={call.aiRequestId}>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.5 }}>
                          {t(`assistant.privacy.callKind.${call.kind ?? 'unknown'}`, {
                            n: callIndex + 1,
                          })}
                        </Typography>
                        {shared && call.askedBy !== null && (
                          <Typography sx={{ ...sub, mb: 0.5 }} data-privacy-asked-by="">
                            {t('assistant.privacy.askedByOnly', {
                              name: personName(call.askedBy, t),
                            })}
                          </Typography>
                        )}
                        <Typography sx={sub}>{t('assistant.privacy.payload')}</Typography>
                        {call.contentRemoved ? (
                          <Typography sx={{ fontSize: 13 }} data-privacy-removed="">
                            {t('assistant.privacy.removed')}
                          </Typography>
                        ) : (
                          <MonoBlock>{call.outboundPayload}</MonoBlock>
                        )}
                        {call.attachments.length > 0 && (
                          <>
                            <Typography sx={{ ...sub, mt: 1 }}>
                              {t('assistant.privacy.filesSent')}
                            </Typography>
                            <Box
                              component="ul"
                              sx={{ m: 0, paddingInlineStart: 2.5, fontSize: 13 }}
                              data-privacy-files=""
                            >
                              {call.attachments.map((file) => (
                                <li key={file.attachmentId}>
                                  <Typography component="span" sx={{ fontSize: 13 }}>
                                    {file.name}
                                  </Typography>
                                  <AttachmentFacts
                                    sizeBytes={file.sizeBytes}
                                    pages={file.pages}
                                    rows={file.rows}
                                    truncated={file.truncated}
                                  />
                                </li>
                              ))}
                            </Box>
                          </>
                        )}
                        {call.omittedAttachments.length > 0 && (
                          <>
                            <Typography sx={{ ...sub, mt: 1 }}>
                              {t('assistant.privacy.filesOmitted')}
                            </Typography>
                            <Box
                              component="ul"
                              sx={{ m: 0, paddingInlineStart: 2.5, fontSize: 13 }}
                              data-privacy-files-omitted=""
                            >
                              {call.omittedAttachments.map((file) => (
                                <li key={file.attachmentId}>
                                  {t('assistant.privacy.fileOmitted', {
                                    name: file.name,
                                    reason: omittedReason(t, file.reason),
                                  })}
                                </li>
                              ))}
                            </Box>
                          </>
                        )}
                        {!call.contentRemoved && call.toolCalls.length > 0 && (
                          <>
                            <Typography sx={{ ...sub, mt: 1 }}>
                              {t('assistant.privacy.toolCalls')}
                            </Typography>
                            {call.toolCalls.map((tool) => (
                              <Fragment key={tool.id}>
                                <Typography
                                  variant="mono"
                                  component="p"
                                  sx={{ fontSize: 12, mt: 0.75 }}
                                >
                                  {tool.name}
                                </Typography>
                                <Typography sx={sub}>{t('assistant.privacy.toolInput')}</Typography>
                                <MonoBlock>{tool.input}</MonoBlock>
                                <Typography sx={sub}>
                                  {t('assistant.privacy.toolResult')}
                                </Typography>
                                <MonoBlock>{tool.result}</MonoBlock>
                              </Fragment>
                            ))}
                          </>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {agentTools && (
            <>
              <Typography component="h3" sx={section}>
                {t('agentTools.privacy.heading')}
              </Typography>
              <AgentCalls calls={privacy.agentCalls} shared={shared} />
            </>
          )}
        </>
      )}
    </Box>
  );
}
